import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Clock, Shield } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [config, setConfig] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/config`)
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { href: "/", label: "Accueil" },
    { href: "/services", label: "Services" },
    { href: "/blog", label: "Blog" },
    { href: "/faq", label: "FAQ" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#0f2744] text-white py-2 text-sm hidden md:block">
        <div className="container-custom flex justify-between items-center">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              7j/7 - 24h/24
            </span>
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-400" />
              Devis gratuit
            </span>
          </div>
          {config && (
            <a 
              href={config.phone_link} 
              onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
              className="flex items-center gap-2 font-semibold hover:text-amber-400 transition-colors"
              data-testid="topbar-phone"
            >
              <Phone className="w-4 h-4" />
              {config.phone}
            </a>
          )}
        </div>
      </div>

      {/* Main navbar */}
      <nav 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled 
            ? "bg-white shadow-lg" 
            : "bg-white/95 backdrop-blur-sm"
        }`}
        data-testid="navbar"
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center gap-3"
              data-testid="navbar-logo"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-[#1a3a5c] to-[#0f2744] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 md:w-7 md:h-7 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-lg md:text-xl font-bold text-[#0f2744] leading-tight">
                  Fichet Dépannage
                </span>
                <span className="text-xs text-gray-500 hidden sm:block">
                  Spécialiste serrures Fichet
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-medium transition-colors ${
                    isActive(link.href)
                      ? "text-[#1a3a5c]"
                      : "text-gray-600 hover:text-[#1a3a5c]"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              {config && (
                <a
                  href={config.phone_link}
                  onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                  className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-lg font-bold shadow-lg transition-all hover:shadow-xl"
                  data-testid="navbar-cta"
                >
                  <Phone className="w-5 h-5" />
                  <span className="hidden lg:inline">Appeler</span>
                  <span className="font-bold">{config.phone}</span>
                </a>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-[#1a3a5c] transition-colors"
              aria-label="Menu"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="container-custom py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`block py-2 font-medium ${
                    isActive(link.href)
                      ? "text-[#1a3a5c]"
                      : "text-gray-600"
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              
              {config && (
                <a
                  href={config.phone_link}
                  onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                  className="btn-cta w-full flex items-center justify-center gap-2 mt-4"
                  data-testid="mobile-cta"
                >
                  <Phone className="w-5 h-5" />
                  Appeler maintenant
                </a>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
